'use client';
import { FC, memo, useCallback, useEffect, useRef, useState } from 'react';

import '../../../styles/components/ui/toastMessage.css';

import InlineIcon from '../../../utils/icons/InlineIcon';

/**
 * Иконите са вградени, а не взети от каталога `Icons.jsx`, защото `Toast` виси на root
 * layout-а през `ToastProvider`. Импортът заради тези четири икони вкарваше 283 KB в
 * layout chunk-а, където после не се ползваше нито байт (одит 2026-08-29).
 *
 * Пътищата са копирани от `generated/iconPaths.js` — при смяна на икона вземи ги оттам.
 */
const SEVERITY_ICON_PATHS: Record<string, string[]> = {
  success: [
    'M938.667 472.747v39.253c-0.139 235.537-191.111 426.423-426.667 426.423-235.641 0-426.667-191.025-426.667-426.667s191.025-426.667 426.667-426.667c62.881 0 122.585 13.603 176.329 38.026l-2.676-1.089',
    'M938.667 170.667l-426.667 427.093-128-128',
  ],
  error: [
    'M938.667 512c0 235.641-191.025 426.667-426.667 426.667s-426.667-191.025-426.667-426.667c0-235.641 191.025-426.667 426.667-426.667s426.667 191.025 426.667 426.667z',
    'M640 384l-256 256',
    'M384 384l256 256',
  ],
  // warning и info делят една икона — така беше и с `<Icon name="alert-circle">`.
  warning: [
    'M938.667 512c0 235.641-191.025 426.667-426.667 426.667s-426.667-191.025-426.667-426.667c0-235.641 191.025-426.667 426.667-426.667s426.667 191.025 426.667 426.667z',
    'M512 341.333v170.667',
    'M512 682.667h0.427',
  ],
  info: [
    'M938.667 512c0 235.641-191.025 426.667-426.667 426.667s-426.667-191.025-426.667-426.667c0-235.641 191.025-426.667 426.667-426.667s426.667 191.025 426.667 426.667z',
    'M512 341.333v170.667',
    'M512 682.667h0.427',
  ],
};

const CLOSE_ICON_PATHS = ['M768 256l-512 512', 'M256 256l512 512'];

import { entranceAnimations, exitAnimations, SEVERITY_STYLES } from './toastMessageHelpers';
import { ToastData } from './toastMessageTypes';

const Toast: FC<{
  data: ToastData;
  // eslint-disable-next-line no-unused-vars
  onClose: (_id: number, _reason?: string) => void;
}> = memo(({ data, onClose }) => {
  const {
    key,
    message,
    title,
    variant,
    severity,
    xIcon,
    position,
    icon,
    autoHideDuration,
    persistent,
  } = data;

  const [progress, setProgress] = useState(100);
  const [exiting, setExiting] = useState(false);

  const frameReference = useRef<number | null>(null);
  const timerIdReference = useRef<number | null>(null);
  const remainingReference = useRef<number>(
    Math.max(0, typeof autoHideDuration === 'number' ? autoHideDuration : 4000),
  );
  const startedAtReference = useRef<number>(0);
  const progressReference = useRef<HTMLDivElement | null>(null);
  const EXIT_ANIM_MS = 700;

  const startProgress = useCallback(() => {
    if (remainingReference.current <= 0) {
      return;
    }
    const start = Date.now();

    const update = () => {
      const elapsed = Date.now() - start;
      const percentage = Math.max(
        0,
        Math.round(((remainingReference.current - elapsed) / autoHideDuration) * 100),
      );
      setProgress(percentage);

      if (percentage > 0) {
        frameReference.current = requestAnimationFrame(update);
      } else {
        setProgress(0);
      }
    };

    update();
  }, [autoHideDuration]);

  const stopProgress = useCallback(() => {
    if (frameReference.current !== null) {
      cancelAnimationFrame(frameReference.current);
      frameReference.current = null;
    }
  }, []);

  const clearTimer = () => {
    if (timerIdReference.current !== null) {
      clearTimeout(timerIdReference.current);
      timerIdReference.current = null;
    }
  };

  const handleCloseToast = useCallback(
    (reason: string) => {
      clearTimer();
      stopProgress();
      setExiting(true);
      setTimeout(() => {
        onClose(key, reason);
      }, EXIT_ANIM_MS);
    },
    [key, onClose, stopProgress],
  );

  const drainAndExit = useCallback(
    (reason: string) => {
      clearTimer();
      stopProgress();
      remainingReference.current = 0;
      setProgress(0);

      const el = progressReference.current;
      if (!el) {
        handleCloseToast(reason);
        return;
      }

      let done = false;
      const finish = () => {
        if (done) {
          return;
        }
        done = true;
        el.removeEventListener('transitionend', onEnd);
        handleCloseToast(reason);
      };

      const onEnd = (event_: TransitionEvent) => {
        if (event_.propertyName === 'width') {
          finish();
        }
      };

      el.addEventListener('transitionend', onEnd, { once: true });

      // Safety fallback in case transitionend doesn’t fire
      setTimeout(finish, 350);
    },
    [handleCloseToast, stopProgress],
  );

  const startTimer = useCallback(() => {
    clearTimer();
    if (remainingReference.current > 0) {
      startedAtReference.current = Date.now();
      timerIdReference.current = Number(
        setTimeout(() => drainAndExit('timeout'), remainingReference.current),
      );
    }
  }, [drainAndExit]);

  useEffect(() => {
    remainingReference.current = Math.max(
      0,
      typeof autoHideDuration === 'number' ? autoHideDuration : 4000,
    );

    if (!persistent) {
      startTimer();
      startProgress();
    }

    return () => {
      clearTimer();
      stopProgress();
    };
  }, [key, autoHideDuration, startTimer, persistent, startProgress, stopProgress]);

  const onMouseEnter = () => {
    if (remainingReference.current <= 0) {
      return;
    }
    const elapsed = Date.now() - startedAtReference.current;
    remainingReference.current = Math.max(0, remainingReference.current - elapsed);

    clearTimer();
    stopProgress();
  };

  const onMouseLeave = () => {
    if (remainingReference.current <= 0) {
      return;
    }
    startedAtReference.current = Date.now();

    startTimer();
    startProgress();
  };

  const styles = SEVERITY_STYLES[severity];
  // const iconName = getIconName(severity); // Reserved for future use

  const inlineOpacity =
    variant === 'outlined' && styles.outlinedOpacity
      ? { opacity: parseFloat(styles.outlinedOpacity as string) }
      : undefined;

  return (
    <div
      className={`toast ${exiting ? exitAnimations[position] : entranceAnimations[position]}`}
      role="status"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className={`toastContent ${severity} ${variant}`} style={inlineOpacity}>
        {icon && (
          <div className="toastIcon">
            {SEVERITY_ICON_PATHS[severity] && (
              <InlineIcon paths={SEVERITY_ICON_PATHS[severity]} size={20} />
            )}
          </div>
        )}

        <div className={`toastBody ${icon ? 'withIcon' : ''}`}>
          <div className="toastContentInner">
            {title && <h6 className="toastTitle">{title}</h6>}
            <h6 className="toastMessage">{message}</h6>
          </div>
        </div>

        {xIcon && (
          <button
            type="button"
            onClick={() => handleCloseToast('closeIcon')}
            className="toastCloseButton"
            aria-label="dismiss"
          >
            <InlineIcon paths={CLOSE_ICON_PATHS} size={16} />
          </button>
        )}
        {!persistent && (
          <>
            <div className="toastProgressContainer" />
            <div
              ref={progressReference}
              className="toastProgress"
              style={{ width: `${progress}%` }}
            />
          </>
        )}
      </div>
    </div>
  );
});
Toast.displayName = 'Toast';

export default Toast;
